import { useFeatureFlagEnabled } from 'posthog-js/react';
import AudienceGroup from 'components/Production/Audience/Group/Group';
import AudienceRow from 'components/Production/Audience/Row/Row';
import Row from 'components/UI/Grid/Row/Row';
import Col from 'components/UI/Grid/Col/Col';
import { formatDate } from '@UIComponents/utils/formatters';
import './ViewershipDetail.scss';
import type { ExtraDataType, Station, ViewershipDetail } from 'watchApi/types';
import ViewershipDetailsExtra from '../ViewershipDetailExtra/ViewershipDetailExtra';
import DailyImpressionsChart from '../DailyImpressionsChart/DailyImpressionsChart';
import { processDemographics } from './demographics';
import ViewershipInfoRow from '../ViewershipInfoRow/ViewershipInfoRow';
import { getStreamingNetworks } from '../utils';

const cbn = 'viewership-detail';

type ViewershipDetailsProps = {
  readonly impressions: number;
  readonly station: Station;
  readonly provider: string;
  readonly premiere: string;
  readonly period: string;
  readonly viewershipDetail: ViewershipDetail | null;
  readonly watchStation: Station;
  readonly notes: string;
};

// Keys that are hidden from the extra data display
const HIDDEN_EXTRA_DATA_KEYS = ['dailyImpressions'];

const hasExtraData = (extraData: ExtraDataType | null | undefined): boolean =>
  Boolean(
    extraData &&
      Object.keys(extraData).filter(
        (key) => !HIDDEN_EXTRA_DATA_KEYS.includes(key),
      ).length,
  );

export const getStationHTML = (
  station: Station | null,
  watchStation: Station | null,
) => {
  const stationName = station
    ? station?.displayName
    : watchStation?.displayName;
  const stationLogo = station ? station.image : watchStation?.image;
  const resizedImage = stationLogo?.replace(/w=\d+&h=\d+/, 'w=32&h=24');

  return (
    <>
      {resizedImage && (
        <img
          className="station-logo"
          src={resizedImage}
          alt={stationName}
          style={{ marginRight: '5px' }}
        />
      )}
      {stationName}
    </>
  );
};
// eslint-disable-next-line complexity, max-lines-per-function
function ViewershipDetails({
  impressions,
  station,
  provider,
  watchStation,
  premiere,
  period,
  viewershipDetail,
  notes,
}: ViewershipDetailsProps) {
  let extraData = viewershipDetail?.extraData ?? null;

  if (typeof extraData === 'string') {
    extraData = JSON.parse(extraData);
  }
  const audience = viewershipDetail
    ? processDemographics(viewershipDetail)
    : null;
  const hasIncome = audience?.income.some((income) => income.value);
  const hasEthnicity = audience?.ethnicity.some((ethnicity) => ethnicity.value);
  const hasAge = audience?.age.some((age) => age.value);
  const hasGender = audience?.gender.some((gender) => gender.value);

  // For streaming, we sometimes have the streaming networks in a JSON inside notes
  const streamingNetworks = getStreamingNetworks(notes);

  // Feature flags
  const hasDailyImpressionsChart = useFeatureFlagEnabled(
    'DailyImpressionsChart',
  );
  const hasViewershipExtraData = useFeatureFlagEnabled('ViewershipExtraData');

  // Extract daily impressions for the chart (behind feature flag)
  const dailyImpressions = extraData?.dailyImpressions as number[] | undefined;

  return (
    <div className={cbn}>
      <div className={`${cbn}__audience`}>
        <div className={`${cbn}__audience_header`}>
          <h3
            className={`${cbn}__audience_title`}
            style={{ fontSize: '20px', fontWeight: '400' }}
          >
            {getStationHTML(station, watchStation)} - {period}
          </h3>
          <hr style={{ marginTop: '0px' }} />
        </div>
        <Row>
          <Col md="6" mtn>
            <section className={`${cbn}__info-group`}>
              <h4 className={`${cbn}__info__title`}>Details</h4>
              <AudienceGroup dataCy={`${cbn}__info__gender`}>
                <ViewershipInfoRow
                  key="info_station"
                  label="Station"
                  value={`${
                    station ? station?.displayName : watchStation?.displayName
                  }, ${station ? station?.country : watchStation?.country}`}
                />
                <ViewershipInfoRow
                  key="info_period"
                  label="Period"
                  value={`${formatDate(premiere)} → ${period}`}
                />
                <ViewershipInfoRow
                  key="info_provider"
                  label="Provider"
                  value={provider}
                />
                {streamingNetworks && (
                  <ViewershipInfoRow
                    key="info_streaming_networks"
                    label="Streaming services"
                    value={streamingNetworks.join(', ')}
                    className="total-row"
                  />
                )}
                {viewershipDetail?.airingIds && (
                  <ViewershipInfoRow
                    key="info_airing_ids"
                    label="Total of airings"
                    value={viewershipDetail?.airingIds?.length}
                    className="total-row"
                  />
                )}
                <ViewershipInfoRow
                  key="info_impressions"
                  label="Total impressions"
                  value={impressions}
                  className="total-row"
                />
              </AudienceGroup>
            </section>
            <hr />
            {hasGender && audience && (
              <section className={`${cbn}__info-group`}>
                <h4 className={`${cbn}__info__title`}>Gender</h4>
                <AudienceGroup dataCy={`${cbn}__info__gender`}>
                  {audience.gender.map((gender) => {
                    if (gender.value) {
                      return (
                        <AudienceRow
                          key={`gender_${gender?.key}`}
                          label={gender.label}
                          value={gender.value}
                        />
                      );
                    }
                    return null;
                  })}
                </AudienceGroup>
              </section>
            )}
          </Col>
          {hasAge && audience && (
            <Col md="6" mtn>
              <section className={`${cbn}__info-group`}>
                <h4 className={`${cbn}__info__title`}>Age</h4>
                <AudienceGroup dataCy={`${cbn}__info__age`}>
                  {audience.age.map((age) => {
                    if (age.value) {
                      return (
                        <AudienceRow
                          key={`age_${age?.key}`}
                          label={age.label}
                          value={age.value}
                        />
                      );
                    }
                    return null;
                  })}
                </AudienceGroup>
              </section>
            </Col>
          )}
        </Row>
        {hasIncome || hasEthnicity ? (
          <>
            <hr />
            <Row>
              {hasIncome && audience && (
                <Col md="6" mtn>
                  <section className={`${cbn}__info-group`}>
                    <h4 className={`${cbn}__info__title`}>Income</h4>
                    <AudienceGroup dataCy={`${cbn}__info__income`}>
                      {audience.income.map((income) => {
                        if (income.value) {
                          return (
                            <AudienceRow
                              key={`income_${income?.key}`}
                              label={income.label}
                              value={income.value}
                            />
                          );
                        }
                        return null;
                      })}
                    </AudienceGroup>
                  </section>
                </Col>
              )}
              {hasEthnicity && audience && (
                <Col md="6" mtn>
                  <section className={`${cbn}__info-group`}>
                    <h4 className={`${cbn}__info__title`}>Ethnicity</h4>
                    <AudienceGroup dataCy={`${cbn}__info__ethnicity`}>
                      {audience.ethnicity.map((ethnicity) => {
                        if (ethnicity.value) {
                          return (
                            <AudienceRow
                              key={`ethnicity_${ethnicity?.key}`}
                              label={ethnicity.label}
                              value={ethnicity.value}
                            />
                          );
                        }
                        return null;
                      })}
                    </AudienceGroup>
                  </section>
                </Col>
              )}
            </Row>
          </>
        ) : null}
        {hasDailyImpressionsChart &&
          dailyImpressions &&
          dailyImpressions.length > 0 && (
            <>
              <hr />
              <DailyImpressionsChart
                dailyImpressions={dailyImpressions}
                premiereDate={premiere}
                period={period}
              />
            </>
          )}
      </div>
      {hasViewershipExtraData &&
        extraData &&
        (hasExtraData(extraData) || viewershipDetail?.description) && (
          <>
            <hr />
            <Row>
              {hasExtraData(extraData) && (
                <Col md="6" mtn>
                  <div className={`${cbn}__extra`}>
                    <h4>Extra data</h4>
                    <div className={`${cbn}__extra__contents`}>
                      <ViewershipDetailsExtra extraData={extraData} />
                    </div>
                  </div>
                </Col>
              )}
              {viewershipDetail?.description && (
                <Col md="6" mtn>
                  <h4>Description</h4>
                  <p>{viewershipDetail.description}</p>
                </Col>
              )}
            </Row>
          </>
        )}
    </div>
  );
}

export default ViewershipDetails;
